import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuthContext } from '../context/AuthContext';
import { useEffect } from 'react'; 
import { supabase } from '../lib/supabase';
import { ProtectedRoute } from '../components/ProtectedRoute';
import TermsOfService from '../pages/TermsOfService';
import PrivacyPolicy from '../pages/PrivacyPolicy';

// Error page component
function ErrorPage({ error }: { error?: Error }) {
  const { user } = useAuthContext();

  useEffect(() => {
    // If we hit a 404 and have no user session, try to refresh auth
    if (!user) {
      supabase.auth.getSession().catch(console.error);
    }
  }, [user]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">Page Not Found</h1>
      <p className="text-gray-600 mb-8 text-center">
        {error?.message || "The page you're looking for doesn't exist or has been moved."}
      </p>
      <a 
        href="/" 
        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Return to Home
      </a>
    </div>
  );
}

// Lazy load pages
const HomePage = lazy(() => import('../pages/HomePage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage').then(m => ({ default: m.default })));
const ProfileCompletion = lazy(() => import('../components/auth/ProfileCompletion').then(m => ({ default: m.ProfileCompletion })));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const TeacherDirectory = lazy(() => import('../pages/TeacherDirectory'));
const TeacherProfile = lazy(() => import('../pages/TeacherProfile').then(m => ({ default: m.TeacherProfile })));
const TeacherDashboard = lazy(() => import('../pages/TeacherDashboard').then(m => ({ default: m.TeacherDashboard })));
const StudentDashboard = lazy(() => import('../pages/StudentDashboard'));
const StudentRequests = lazy(() => import('../pages/teacher/StudentRequests').then(m => ({ default: m.StudentRequests })));
const MyBookings = lazy(() => import('../pages/student/MyBookings').then(m => ({ default: m.MyBookings })));
const MessagesPage = lazy(() => import('../pages/MessagesPage'));
const Unauthorized = lazy(() => import('../pages/Unauthorized').then(m => ({ default: m.Unauthorized })));
const JobBoard = lazy(() => import('../pages/JobBoard'));
const AdminDashboard = lazy(() => import('../pages/AdminDashboard'));
const NewJobPost = lazy(() => import('../pages/NewJobPost'));
const JobPost = lazy(() => import('../pages/JobPost'));

// Router configuration
export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />
      },
      {
        path: 'reset-password',
        element: <ResetPassword />
      },
      {
        path: 'complete-profile',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProtectedRoute allowedRoles={['student']}>
              <ProfileCompletion />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'teachers',
        children: [
          {
            index: true,
            element: <TeacherDirectory />
          },
          {
            path: ':id',
            element: <TeacherProfile />
          }
        ]
      },
      {
        path: 'unauthorized',
        element: <Unauthorized />
      },
      {
        path: 'profile',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: 'messages',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            )
          },
          {
            path: ':conversationId',
            element: (
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            )
          }
        ]
      },
      {
        path: 'teacher',
        children: [
          {
            path: 'dashboard',
            element: (
              <ProtectedRoute allowedRoles={['teacher']}>
                <TeacherDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: 'requests',
            element: (
              <ProtectedRoute allowedRoles={['teacher']}>
                <StudentRequests />
              </ProtectedRoute>
            )
          }
        ]
      },
      {
        path: 'student',
        children: [
          {
            path: 'dashboard',
            element: (
              <ProtectedRoute allowedRoles={['student']}>
                <StudentDashboard />
              </ProtectedRoute>
            )
          },
          {
            path: 'bookings',
            element: (
              <ProtectedRoute allowedRoles={['student']}>
                <MyBookings />
              </ProtectedRoute>
            )
          }
        ]
      },
      {
        path: 'jobs',
        children: [
          {
            index: true,
            element: <JobBoard />
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute allowedRoles={['student']}>
                <NewJobPost />
              </ProtectedRoute>
            )
          },
          {
            path: ':id',
            element: <JobPost />
          }
        ]
      },
      {
        path: 'admin',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          </Suspense>
        )
      },
      {
        path: '*',
        element: <Navigate to="/" replace />
      },
      {
        path: 'terms',
        element: <TermsOfService />
      },
      {
        path: 'privacy',
        element: <PrivacyPolicy />
      }
    ]
  }
]);

export default router;